import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProductCard = ({ product, onAddToWishlist, onQuickView, onAddToCart, userTier }) => {
  const [isWishlisted, setIsWishlisted] = useState(product?.isWishlisted || false);

  const handleWishlistToggle = () => {
    setIsWishlisted(!isWishlisted);
    onAddToWishlist?.(product?.id, !isWishlisted);
  };

  const getTierDiscount = () => {
    const tierDiscounts = {
      'Intro': 5,
      'All Access': 10,
      'Flex': 15,
      'Premium': 20
    };
    return tierDiscounts?.[userTier] || 0;
  };

  const getDiscountedPrice = () => {
    const discount = getTierDiscount();
    return product?.price * (1 - discount / 100);
  };

  const renderRating = () => {
    return (
      <div className="flex items-center space-x-1">
        {[...Array(5)]?.map((_, i) => (
          <Icon
            key={i}
            name="Star"
            size={14}
            className={i < Math.floor(product?.rating) ? 'text-accent fill-current' : 'text-muted'}
          />
        ))}
        <span className="text-sm text-muted-foreground ml-1">
          ({product?.reviewCount})
        </span>
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-wellness hover:shadow-wellness-md transition-wellness group">
      {/* Product Image */}
      <div className="relative overflow-hidden rounded-t-lg">
        <Image
          src={product?.image}
          alt={product?.name}
          className="w-full h-48 object-cover group-hover:scale-105 transition-wellness-slow"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col space-y-2">
          {product?.isFeatured && (
            <span className="bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-medium">
              Featured
            </span>
          )}
          {product?.isNew && (
            <span className="bg-success text-success-foreground px-2 py-1 rounded-full text-xs font-medium">
              New
            </span>
          )}
          {getTierDiscount() > 0 && (
            <span className="bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-medium">
              {getTierDiscount()}% Off
            </span>
          )}
        </div>

        {/* Wishlist Button */}
        <button
          onClick={handleWishlistToggle}
          className="absolute top-3 right-3 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-wellness"
        >
          <Icon
            name="Heart"
            size={16}
            className={isWishlisted ? 'text-error fill-current' : 'text-muted-foreground'}
          />
        </button>

        {/* Quick View Button */}
        <button
          onClick={() => onQuickView?.(product)}
          className="absolute inset-0 bg-black/0 hover:bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-wellness"
        >
          <div className="bg-white rounded-full p-2">
            <Icon name="Eye" size={20} className="text-foreground" />
          </div>
        </button>
      </div>
      {/* Product Info */}
      <div className="p-4">
        {/* Category & Center */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-primary font-medium bg-primary/10 px-2 py-1 rounded-full">
            {product?.category}
          </span>
          <span className="text-xs text-muted-foreground">
            {product?.centerName}
          </span>
        </div>

        {/* Product Name */}
        <h3 className="font-heading font-semibold text-foreground mb-2 line-clamp-2">
          {product?.name}
        </h3>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
          {product?.description}
        </p>

        {/* Rating */}
        <div className="mb-3">
          {renderRating()}
        </div>

        {/* Pricing */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            {getTierDiscount() > 0 ? (
              <>
                <span className="text-lg font-bold text-foreground">
                  ${getDiscountedPrice()?.toFixed(2)}
                </span>
                <span className="text-sm text-muted-foreground line-through">
                  ${product?.price?.toFixed(2)}
                </span>
              </>
            ) : (
              <span className="text-lg font-bold text-foreground">
                ${product?.price?.toFixed(2)}
              </span>
            )}
          </div>
          
          {product?.originalPrice && product?.originalPrice > product?.price && (
            <span className="text-xs text-success font-medium">
              Save ${(product?.originalPrice - product?.price)?.toFixed(2)}
            </span>
          )}
        </div>

        {/* Availability */}
        <div className="flex items-center space-x-2 mb-4">
          <div className={`w-2 h-2 rounded-full ${
            product?.availability === 'available' ? 'bg-success' : 
            product?.availability === 'limited' ? 'bg-warning' : 'bg-error'
          }`}></div>
          <span className="text-sm text-muted-foreground capitalize">
            {product?.availability === 'available' ? 'In Stock' :
             product?.availability === 'limited' ? 'Limited Stock' : 'Out of Stock'}
          </span>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => onAddToCart?.(product)}
            disabled={product?.availability === 'unavailable'}
            className="flex-1"
            iconName="ShoppingCart"
            iconPosition="left"
          >
            Add to Cart
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onQuickView?.(product)}
            iconName="Eye"
          />
        </div>

        {/* Tier Benefits */}
        {userTier && getTierDiscount() > 0 && (
          <div className="mt-3 p-2 bg-primary/5 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="Award" size={14} className="text-primary" />
              <span className="text-xs text-primary font-medium">
                {userTier} Member: {getTierDiscount()}% discount applied
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;